import {
	isSchemaActionRef,
	isSchemaConstAny,
	isSchemaConstArray,
	isSchemaConstBoolean,
	isSchemaConstEnum,
	isSchemaConstFloat,
	isSchemaConstInteger,
	isSchemaConstMap,
	isSchemaConstObject,
	isSchemaConstPassword,
	isSchemaConstSecret,
	isSchemaConstString,
	isSchemaExpression,
	isSchemaIconName,
	isSchemaIntegrationRef,
	isSchemaOneOf,
	isSchemaOptGroup,
	isSchemaRecursive,
	isSchemaResponsiveValue,
	isSchemaRouteRef,
	isSchemaStringWithConst,
	isSchemaValue,
	isSchemaViewRef,
	TGenericBlueprintSchema
} from "@adapptio/lib-blueprint";
import { controlHiddenProp } from "../helpers/controlHiddenProp";

export const mapperDescriptor = (schema: TGenericBlueprintSchema, padding = "") => {
	if (isSchemaValue(schema) && !controlHiddenProp(schema)) {
		return mapperDescriptor(schema.constSchema, padding);
	}

	if (isSchemaResponsiveValue(schema)) {
		return mapperDescriptor(schema.opts.value, padding + "\t");
	}

	if (isSchemaConstMap(schema)) {
		return `Map<String, ${mapperDescriptor(schema.opts.value, padding)}>`;
	}

	if (isSchemaIntegrationRef(schema)) {
		return (
			"{\n" +
			padding +
			"\t" +
			"integrationType: String,\n" +
			padding +
			"\t" +
			"functionName: String,\n" +
			padding +
			"\t" +
			"integrationId: String, // id of Integration\n" +
			padding +
			"\t" +
			"params: Map<String, Any>\n" +
			padding +
			"}"
		);
	}

	if (isSchemaActionRef(schema)) {
		return (
			"{\n" +
			padding +
			"\t" +
			"actionId: String, // id of Action\n" +
			padding +
			"\t" +
			"params: Map<String, Any>\n" +
			padding +
			"}"
		);
	}

	if (isSchemaRouteRef(schema)) {
		return (
			"{\n" +
			padding +
			"\t" +
			"name: String,\n" +
			padding +
			"\t" +
			"params: Map<String, String>?\n" +
			padding +
			"}"
		);
	}

	if (isSchemaRecursive(schema)) {
		return mapperDescriptor(schema.opts.value(schema));
	}

	if (isSchemaViewRef(schema)) {
		return (
			"{\n" +
			padding +
			"\t" +
			"viewId: String, // id of View\n" +
			padding +
			"\t" +
			"params: Map<String, Any>\n" +
			padding +
			"}"
		);
	}

	if (isSchemaOptGroup(schema)) {
		return mapperDescriptor(schema.opts["value"], padding);
	}

	if (isSchemaOneOf(schema)) {
		const child = schema.opts.types;
		const opts = schema.opts;
		const optional = !opts.constraints?.required ? "?" : "";
		const keys = [],
			props = [];

		Object.keys(child).map((key) => {
			keys.push(`"${key}"`);
			props.push(key);
		});

		const bodyObj = props
			.map((prop) => {
				if (!controlHiddenProp(child[prop].value)) {
					return (
						padding + "\t\t" + prop + ": " + mapperDescriptor(child[prop].value, padding + "\t\t")
					);
				}
			})
			.filter((el) => el !== undefined);

		const stingObj =
			"{" +
			(props.length > 0 ? "\n" : "") +
			bodyObj.join(",\n") +
			(props.length > 0 ? "\n" : "") +
			padding +
			"\t}";

		return (
			"{\n" +
			padding +
			"\t" +
			"type: " +
			keys.join("|") +
			",\n" +
			padding +
			"\t" +
			"value: " +
			stingObj +
			"\n" +
			padding +
			"}" +
			optional
		);
	}

	if (isSchemaConstArray(schema)) {
		const opts = schema.opts;
		const optional = !opts.constraints?.required ? "?" : "";
		return "Array<" + mapperDescriptor(schema.opts.items, padding) + ">" + optional;
	}

	if (isSchemaConstObject(schema)) {
		const child = schema.opts.props;
		const childProps = Object.keys(child);
		const opts = schema.opts;
		const optional = !opts.constraints?.required ? "?" : "";

		const bodyObj = childProps
			.map((prop) => {
				if (!controlHiddenProp(child[prop].schema)) {
					if (prop === "items") {
						return padding + "\t" + "child: self";
					} else {
						return (
							padding +
							"\t" +
							prop +
							": " +
							mapperDescriptor(child[prop].schema, padding + "\t")
						);
					}
				}
			})
			.filter((el) => el !== undefined);

		const stingObj =
			"{" +
			(childProps.length > 0 ? "\n" : "") +
			bodyObj.join(",\n") +
			(childProps.length > 0 ? "\n" : "") +
			padding +
			"}" +
			optional;

		return stingObj.replace('// "<package_name>/<icon_name>",', '// "<package_name>/<icon_name>"');
	}

	if (isSchemaConstInteger(schema)) {
		const optional = !schema.opts?.constraints?.required ? "?" : "";
		return `Integer${optional}`;
	}

	if (
		isSchemaConstPassword(schema) ||
		isSchemaConstSecret(schema) ||
		isSchemaConstString(schema) ||
		isSchemaStringWithConst(schema)
	) {
		const optional = !schema.opts?.constraints?.required ? "?" : "";
		return `String${optional}`;
	}

	if (isSchemaIconName(schema)) {
		const optional = !schema.opts?.constraints?.required ? "?" : "";
		return `String${optional}, // "<package_name>/<icon_name>"`;
	}

	if (isSchemaConstFloat(schema)) {
		const optional = !schema.opts?.constraints?.required ? "?" : "";
		return `Float${optional}`;
	}

	if (isSchemaConstBoolean(schema)) {
		const optional = !schema.opts?.constraints?.required ? "?" : "";
		return `Boolean${optional}`;
	}

	if (isSchemaConstEnum(schema)) {
		const opts = schema.opts;
		const value = opts.options
			.map((item) => {
				if (typeof item.value === "string") {
					return `"${item.value}"`;
				} else {
					return `${item.value}`;
				}
			})
			.join("|");

		const optional = !opts?.constraints?.required ? "?" : "";

		return "Enum<" + value + ">" + optional;
	}

	if (isSchemaExpression(schema)) {
		return `<expression>`;
	}

	if (isSchemaConstAny(schema)) {
		const opts = schema.opts;
		const optional = !opts.constraints?.required ? "?" : "";
		if (opts.allowedTypes) {
			const valueArr = Object.keys(opts.allowedTypes).map((key) => `${key}${optional}`);
			const valueStr = valueArr.join("|");
			return valueStr;
		} else {
			return `Any${optional}`;
		}
	}
};
